import { useCallback, useEffect, useState } from 'react';
import { clientScopedFetch } from '../lib/authedFetch';
import { useViewingAs } from '../hooks/useViewingAs';

const API_BASE = process.env.REACT_APP_BACKEND_URL || 'https://autopilot-backend-pn9r.onrender.com';

async function readJson(res) {
  const body = await res.json().catch(() => ({}));
  if (!res.ok || body.ok === false) {
    const err = new Error(body.error || `HTTP ${res.status}`);
    err.code = body.code;
    throw err;
  }
  return body;
}

const fmt = (n) => Number(n || 0).toLocaleString('en-US');
const fmtRange = (r) => {
  if (!r) return '';
  const o = { month: 'short', day: 'numeric' };
  const d = (s) => new Date(`${s}T12:00:00Z`).toLocaleDateString(undefined, o);
  return `${d(r.start)} – ${d(r.end)}`;
};
const monthLabel = (ym) => new Date(`${ym}-15T12:00:00Z`).toLocaleDateString(undefined, { month: 'long', year: 'numeric' });

// The last `n` complete months, newest first, as YYYY-MM, from the server's
// idea of the latest complete month (it knows the client's timezone).
function recentMonths(latest, n = 6) {
  if (!latest) return [];
  let [y, m] = latest.split('-').map(Number);
  const out = [];
  for (let i = 0; i < n; i++) {
    out.push(`${y}-${String(m).padStart(2, '0')}`);
    m -= 1; if (m === 0) { m = 12; y -= 1; }
  }
  return out;
}

// Change vs the previous 28 days, in words and an arrow — never colour alone.
function Change({ pct }) {
  if (pct === null || pct === undefined) return <span style={{ fontSize:11, color:'var(--text-muted)' }}>no earlier data</span>;
  if (pct === 0) return <span style={{ fontSize:11, color:'var(--text-muted)' }}>→ same as before</span>;
  const up = pct > 0;
  return (
    <span style={{ fontSize:11, color:'var(--text-secondary)' }} aria-label={`${up ? 'up' : 'down'} ${Math.abs(pct)} percent`}>
      {up ? '▲' : '▼'} {Math.abs(pct)}% vs previous 28 days
    </span>
  );
}

const tile = { background:'var(--bg-card2, var(--bg-surface))', border:'1px solid var(--border)', borderRadius:10, padding:'12px 14px', minWidth:0 };

// Google Business numbers for Post Performance: Search and Maps views, calls,
// directions and website clicks for the last 28 days against the 28 before,
// bookings / food orders / menu views only when nonzero (the server leaves
// them out otherwise), and the monthly search-keyword report.
//
// Reads go through clientScopedFetch and key on viewingAsClientId as well as
// clientId — the Phase 3 rule every panel follows, so an admin switching who
// they view sees that client's numbers. The server caches for 6 hours.
export default function GoogleStats({ clientId }) {
  const { viewingAsClientId } = useViewingAs();
  const [stats, setStats]       = useState(null);
  const [statsError, setStatsError] = useState(null);
  const [kw, setKw]             = useState(null);
  const [kwError, setKwError]   = useState(null);
  const [month, setMonth]       = useState(null);   // null = server default (last complete month)
  const [latestMonth, setLatestMonth] = useState(null);
  const [showAllKw, setShowAllKw] = useState(false);

  const loadStats = useCallback(async () => {
    setStats(null); setStatsError(null);
    try { setStats(await readJson(await clientScopedFetch(`${API_BASE}/api/google-business/stats`))); }
    catch (err) { setStatsError({ message: err.message, reconnect: err.code === 'gbp_reconnect', comingSoon: err.code === 'gbp_not_available' }); }
  }, []);

  const loadKeywords = useCallback(async (m) => {
    setKw(null); setKwError(null); setShowAllKw(false);
    try {
      const d = await readJson(await clientScopedFetch(`${API_BASE}/api/google-business/keywords${m ? `?month=${m}` : ''}`));
      setKw(d);
      setLatestMonth(d.latestMonth || null);
    } catch (err) { setKwError(err.message); }
  }, []);

  useEffect(() => {
    if (!clientId) return;
    setMonth(null);
    loadStats();
    loadKeywords(null);
  }, [clientId, viewingAsClientId, loadStats, loadKeywords]);

  const pickMonth = (m) => { setMonth(m); loadKeywords(m); };
  const keywords = kw ? kw.keywords : [];
  const shownKw = showAllKw ? keywords : keywords.slice(0, 15);

  // Locked (admin-only until Google verification): show nothing at all. The
  // panel only mounts this when Google is enabled, so this is the backstop.
  if (statsError && statsError.comingSoon) return null;

  return (
    <section data-testid="google-stats" style={{ display:'flex', flexDirection:'column', gap:14 }}>
      <div style={{ display:'flex', alignItems:'baseline', gap:10, flexWrap:'wrap' }}>
        <h3 style={{ margin:0, fontSize:16, color:'var(--text-primary)' }}>Google Business</h3>
        {stats && <span style={{ fontSize:12, color:'var(--text-muted)' }}>{fmtRange(stats.range)}</span>}
      </div>

      {!stats && !statsError && <div style={{ fontSize:13, color:'var(--text-muted)' }}>Loading Google numbers…</div>}
      {statsError && (
        <div className="cq-error">
          {statsError.reconnect ? 'Reconnect Google Business on the Connections page to see these numbers.' : `Couldn’t load Google numbers: ${statsError.message}`}
        </div>
      )}

      {stats && (
        <>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(150px, 1fr))', gap:10 }}>
            {stats.stats.map(s => (
              <div key={s.key} style={tile} data-testid={`gbp-stat-${s.key}`}>
                <div style={{ fontSize:12, color:'var(--text-muted)' }}>{s.label}</div>
                <div style={{ fontSize:24, fontWeight:700, color:'var(--text-primary)', lineHeight:1.3 }}>{fmt(s.current)}</div>
                <Change pct={s.change_pct} />
              </div>
            ))}
          </div>
          <div style={{ fontSize:11, color:'var(--text-muted)', lineHeight:1.5 }}>
            Views count each person once a day. Google can take a few days to finish counting, so the most recent days may still go up.
          </div>
        </>
      )}

      <div style={{ display:'flex', alignItems:'center', gap:10, flexWrap:'wrap', marginTop:6 }}>
        <h4 style={{ margin:0, fontSize:14, color:'var(--text-primary)' }}>What people searched to find you</h4>
        {latestMonth && (
          <select aria-label="Keyword month" value={month || latestMonth} onChange={e => pickMonth(e.target.value)}
            style={{ padding:'4px 8px', borderRadius:6, background:'var(--bg-surface)', border:'1px solid var(--border)', color:'var(--text-primary)', fontSize:12 }}>
            {recentMonths(latestMonth).map(m => <option key={m} value={m}>{monthLabel(m)}</option>)}
          </select>
        )}
      </div>
      {!kw && !kwError && <div style={{ fontSize:13, color:'var(--text-muted)' }}>Loading search keywords…</div>}
      {kwError && <div className="cq-error">Couldn’t load search keywords: {kwError}</div>}
      {kw && keywords.length === 0 && <div style={{ fontSize:13, color:'var(--text-muted)' }}>Google has no search keywords for {monthLabel(kw.month)} yet.</div>}
      {kw && keywords.length > 0 && (
        <>
          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }} data-testid="gbp-keywords">
            <thead>
              <tr>
                <th style={{ textAlign:'left', padding:'6px 4px', color:'var(--text-muted)', fontWeight:600, borderBottom:'1px solid var(--border)' }}>Search</th>
                <th style={{ textAlign:'right', padding:'6px 4px', color:'var(--text-muted)', fontWeight:600, borderBottom:'1px solid var(--border)' }}>People</th>
              </tr>
            </thead>
            <tbody>
              {shownKw.map(k => (
                <tr key={k.keyword}>
                  <td style={{ padding:'6px 4px', color:'var(--text-secondary)', borderBottom:'1px solid var(--border)' }}>{k.keyword}</td>
                  <td style={{ padding:'6px 4px', textAlign:'right', color: k.value === null ? 'var(--text-muted)' : 'var(--text-primary)', borderBottom:'1px solid var(--border)', whiteSpace:'nowrap' }}>{k.label}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {keywords.some(k => k.value === null) && (
            <div style={{ fontSize:11, color:'var(--text-muted)', lineHeight:1.5 }}>
              “Under 15” (or any “under” number) means Google only says it was fewer than that many people — it doesn’t share exact counts that small.
            </div>
          )}
          {keywords.length > 15 && (
            <button className="cal-post-btn cal-post-btn-ghost" style={{ alignSelf:'flex-start' }} onClick={() => setShowAllKw(v => !v)}>
              {showAllKw ? 'Show fewer' : `Show all ${keywords.length}${kw.truncated ? '+' : ''}`}
            </button>
          )}
        </>
      )}
    </section>
  );
}
